import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { QRCodeSVG } from "qrcode.react";
import SearchInput from "../common/SearchInput";
import FunctionButton from "../common/FunctionButton";
import { publicApi } from "../../services/api";
import { saveBlobAsFile } from "../../services/FileDownloadUtil";

/*
  OTP 등록 / 재등록 모달.

  브라우저 팝업(window.open) 대신 화면 내 모달로 처리한다.
  Secret Key 는 QR 과 수동 등록 키 두 가지로 제공한다. QR 을 못 쓰는 환경이 있기 때문이다.
*/

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;

const PopupWrap = styled.div`
  width: 450px;
  border: 1px solid #e4dbd2;
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  text-align: left;
`;

const PopupHeader = styled.div`
  background-color: #e4dcd3;
  font-size: 16px;
  font-weight: bold;
  height: 53px;
  line-height: 53px;
  padding: 0 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  button {
    border: 0;
    background: none;
    color: #888;
    font-size: 16px;
    cursor: pointer;
  }
`;

const PopupContents = styled.div`
  padding: 20px 22px;

  .otp_notice {
    background: #eef4fb;
    border: 1px solid #cfe0f5;
    border-left: 4px solid #002c5f;
    border-radius: 4px;
    padding: 10px 12px;
    margin-bottom: 16px;
    font-size: 12.5px;
    line-height: 1.6;
    color: #24486e;
  }

  .otp_section {
    font-size: 14px;
    font-weight: bold;
    margin: 0 0 6px;
    padding-left: 14px;
    position: relative;
  }

  .otp_section:before {
    content: "";
    position: absolute;
    left: 0;
    top: 4px;
    width: 8px;
    height: 8px;
    background: #002c5f;
  }

  .otp_guide {
    text-align: center;
    color: #777;
    font-size: 12.5px;
    margin: 4px 0 14px;
  }

  .otp_qr {
    display: flex;
    justify-content: center;
    margin-bottom: 16px;
  }

  .otp_secret {
    background: #fdeef0;
    border-radius: 4px;
    padding: 8px 10px;
    text-align: center;
    font-size: 12.5px;
    color: #c0392b;
    margin-bottom: 16px;
    word-break: break-all;
  }

  .otp_secret span {
    color: #555;
    margin-right: 4px;
  }

  .otp_input_row {
    display: flex;
    align-items: center;
    border: 1px solid #e2e2e2;
    border-radius: 4px;
    overflow: hidden;
  }

  .otp_input_row .otp_label {
    width: 110px;
    background: #f6f6f6;
    padding: 12px 10px;
    font-size: 13px;
    border-right: 1px solid #e2e2e2;
    white-space: nowrap;
  }

  .otp_input_row .otp_label i {
    color: #c0392b;
    font-style: normal;
    margin-right: 3px;
  }

  .otp_timer {
    text-align: right;
    font-size: 12px;
    color: #888;
    margin-top: 8px;
  }

  .otp_message {
    min-height: 18px;
    margin-top: 8px;
    font-size: 12.5px;
    color: #c62828;
  }

  .otp_guide_file {
    border-top: 1px solid #eee;
    margin-top: 14px;
    padding-top: 12px;
  }

  .otp_guide_file button {
    width: auto;
    padding: 0;
    margin: 4px 0 0;
    border: 0;
    background: transparent;
    font-size: 13px;
    text-align: left;
    text-decoration: underline;
    color: #002c5f;
    cursor: pointer;
    word-break: break-all;
  }
`;

const ButtonBottom = styled.div`
  display: flex;
  justify-content: center;
  padding: 4px 22px 20px;
`;

const formatRemain = (seconds) => {
  const safe = Math.max(0, seconds);
  const min = String(Math.floor(safe / 60)).padStart(2, "0");
  const sec = String(safe % 60).padStart(2, "0");

  return `${min}:${sec}`;
};

const OtpRegisterModal = ({
  mode,
  otpAuthUri,
  secretKey,
  code,
  onCodeChange,
  onRegister,
  onClose,
  remainSeconds,
  message,
}) => {
  const { t } = useTranslation();
  const [guideFileName, setGuideFileName] = useState("");
  const [downloadError, setDownloadError] = useState(false);

  const isFirstRegister = mode === "REGISTER";

  // 안내 파일은 서버 설정에 지정된 파일 하나이며, 파일이 없으면 다운로드를 표출하지 않는다.
  useEffect(() => {
    let isMounted = true;

    publicApi
      .post("/user/otp/guide", {})
      .then((response) => {
        if (isMounted) {
          setGuideFileName(response?.data?.fileName || "");
        }
      })
      .catch((error) => {
        // 안내 파일 조회 실패가 OTP 등록을 막지 않도록 한다.
        console.error("Error loading OTP guide file:", error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleGuideDownload = async () => {
    setDownloadError(false);

    try {
      const response = await publicApi.post(
        "/user/otp/guide/download",
        {},
        { responseType: "blob" }
      );
      saveBlobAsFile(response.data, guideFileName);
    } catch (error) {
      console.error("Error downloading OTP guide file:", error);
      setDownloadError(true);
    }
  };

  const activeEnter = (e) => {
    if (e.key === "Enter") {
      onRegister();
    }
  };

  return (
    <Backdrop>
      <PopupWrap>
        <PopupHeader>
          <span>{isFirstRegister ? t("L1127") : t("L1128")}</span>
          <button type="button" onClick={onClose} aria-label="Close">
            &#10005;
          </button>
        </PopupHeader>

        <PopupContents>
          {/* 미등록 안내는 최초 등록에서만 표시한다. */}
          {isFirstRegister && <div className="otp_notice">{t("L1116")}</div>}

          <div className="otp_section">{t("L1117")}</div>
          <div className="otp_guide">{t("L1119")}</div>

          <div className="otp_qr">
            {otpAuthUri && <QRCodeSVG value={otpAuthUri} size={176} />}
          </div>

          <div className="otp_secret">
            <span>{t("L1120")} :</span>
            {secretKey}
          </div>

          <div className="otp_input_row">
            <div className="otp_label">
              <i>*</i>
              {t("L1126")}
            </div>
            <SearchInput
              value={code}
              width="100%"
              maxLength={6}
              placeholder={t("L1121")}
              style={{ height: "42px", border: "0", backgroundColor: "#fff" }}
              onChange={onCodeChange}
              onKeyDown={activeEnter}
              inputMode="numeric"
              autoFocus
            ></SearchInput>
          </div>

          <div className="otp_timer">
            {t("L1122")} {formatRemain(remainSeconds)}
          </div>

          <div className="otp_message">
            {downloadError ? t("L0957") : message}
          </div>

          {/* 안내 파일 다운로드. 서버에 안내 파일이 없으면 표출하지 않는다. */}
          {guideFileName && (
            <div className="otp_guide_file">
              <div className="otp_section">{t("L0939")}</div>
              <button type="button" onClick={handleGuideDownload}>
                {guideFileName}
              </button>
            </div>
          )}
        </PopupContents>

        <ButtonBottom>
          <FunctionButton
            value={isFirstRegister ? t("L1127") : t("L1128")}
            onClick={onRegister}
          ></FunctionButton>
          <FunctionButton
            className="bg_gray"
            value={t("L0005")}
            onClick={onClose}
          ></FunctionButton>
        </ButtonBottom>
      </PopupWrap>
    </Backdrop>
  );
};

export default OtpRegisterModal;
